import { useState, useEffect } from 'react';
import { apiFetch } from '@/lib/api';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Building2, User, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { PlatformHeader } from '@/components/platform';

type SubscriptionTier = 'FREE' | 'STANDARD' | 'PREMIUM' | 'ENTERPRISE';

interface FormData {
  name: string;
  slug: string;
  billingEmail: string;
  subscriptionTier: SubscriptionTier;
  isTrial: boolean;
  trialEndsAt: string;
  initialAdmin: {
    email: string;
    firstName: string;
    lastName: string;
  };
}

interface FormErrors {
  [key: string]: string | undefined;
}

const TIER_LIMITS: Record<SubscriptionTier, { maxUsers: number; maxMachines: number; maxWarehouses: number }> = {
  FREE: { maxUsers: 5, maxMachines: 10, maxWarehouses: 2 },
  STANDARD: { maxUsers: 50, maxMachines: 100, maxWarehouses: 20 },
  PREMIUM: { maxUsers: 200, maxMachines: 500, maxWarehouses: 100 },
  ENTERPRISE: { maxUsers: 999999, maxMachines: 999999, maxWarehouses: 999999 },
};

function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 100);
}

export default function TenantCreate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [form, setForm] = useState<FormData>({
    name: '',
    slug: '',
    billingEmail: '',
    subscriptionTier: 'STANDARD',
    isTrial: false,
    trialEndsAt: '',
    initialAdmin: {
      email: '',
      firstName: '',
      lastName: '',
    },
  });

  useEffect(() => {
    if (!form.slug) {
      setSlugAvailable(null);
      return;
    }

    const timer = setTimeout(async () => {
      setCheckingSlug(true);
      try {
        const res = await apiFetch(`/api/v1/platform/tenants/check-slug?slug=${encodeURIComponent(form.slug)}`);
        if (res.ok) {
          const data = await res.json();
          setSlugAvailable(data.available);
        }
      } catch {
        setSlugAvailable(null);
      } finally {
        setCheckingSlug(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [form.slug]);

  const updateField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setErrors(prev => ({ ...prev, [key]: undefined }));
  };

  const updateAdminField = (key: keyof FormData['initialAdmin'], value: string) => {
    setForm(prev => ({
      ...prev,
      initialAdmin: { ...prev.initialAdmin, [key]: value },
    }));
    setErrors(prev => ({ ...prev, [`initialAdmin.${key}`]: undefined }));
  };

  const handleNameChange = (name: string) => {
    updateField('name', name);
    if (!form.slug || form.slug === generateSlug(form.name)) {
      updateField('slug', generateSlug(name));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!form.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!form.slug.trim()) {
      newErrors.slug = 'Slug is required';
    } else if (!/^[a-z0-9-]+$/.test(form.slug)) {
      newErrors.slug = 'Slug must be lowercase alphanumeric with hyphens';
    } else if (slugAvailable === false) {
      newErrors.slug = 'Slug is already in use';
    }

    if (!form.billingEmail.trim()) {
      newErrors.billingEmail = 'Billing email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.billingEmail)) {
      newErrors.billingEmail = 'Invalid email format';
    }

    if (form.isTrial && !form.trialEndsAt) {
      newErrors.trialEndsAt = 'Trial end date is required for trial accounts';
    }

    if (!form.initialAdmin.email.trim()) {
      newErrors['initialAdmin.email'] = 'Admin email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.initialAdmin.email)) {
      newErrors['initialAdmin.email'] = 'Invalid email format';
    }

    if (!form.initialAdmin.firstName.trim()) {
      newErrors['initialAdmin.firstName'] = 'First name is required';
    }

    if (!form.initialAdmin.lastName.trim()) {
      newErrors['initialAdmin.lastName'] = 'Last name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validate()) return;

    setLoading(true);

    try {
      const payload = {
        ...form,
        trialEndsAt: form.isTrial && form.trialEndsAt ? new Date(form.trialEndsAt).toISOString() : undefined,
      };

      const res = await apiFetch('/api/v1/platform/tenants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json();
        if (data.error) {
          setSubmitError(data.error);
        } else if (data.details) {
          const errorMessages = data.details.map((d: { message: string }) => d.message).join(', ');
          setSubmitError(errorMessages);
        } else {
          setSubmitError('Failed to create tenant');
        }
        return;
      }

      const result = await res.json();
      navigate(`/platform/tenants/${result.organization.id}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const tierLimits = TIER_LIMITS[form.subscriptionTier];

  return (
    <div className="min-h-screen bg-gray-50">
      <PlatformHeader />
      <div className="space-y-6">
        <div className="mb-6">
          <Link
            to="/platform/tenants"
            className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Tenants
          </Link>
          <h1 className="text-2xl font-bold">Create Tenant</h1>
          <p className="text-muted-foreground">Set up a new tenant organization</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="h-5 w-5" />
                Organization Details
              </CardTitle>
              <CardDescription>Basic information about the tenant organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Organization Name *</Label>
                <Input
                  id="name"
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="Acme Vending Ltd"
                />
                {errors.name && <p className="text-sm text-red-600">{errors.name}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">Slug *</Label>
                <div className="relative">
                  <Input
                    id="slug"
                    value={form.slug}
                    onChange={(e) => updateField('slug', e.target.value.toLowerCase())}
                    placeholder="acme-vending"
                  />
                  {checkingSlug && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                </div>
                {slugAvailable === true && form.slug && (
                  <p className="text-sm text-green-600">Slug is available</p>
                )}
                {slugAvailable === false && form.slug && (
                  <p className="text-sm text-red-600">Slug is already in use</p>
                )}
                {errors.slug && <p className="text-sm text-red-600">{errors.slug}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="billingEmail">Billing Email *</Label>
                <Input
                  id="billingEmail"
                  type="email"
                  value={form.billingEmail}
                  onChange={(e) => updateField('billingEmail', e.target.value)}
                  placeholder="billing@acmevending.com"
                />
                {errors.billingEmail && <p className="text-sm text-red-600">{errors.billingEmail}</p>}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <CreditCard className="h-5 w-5" />
                Subscription
              </CardTitle>
              <CardDescription>Subscription tier and trial settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="tier">Subscription Tier *</Label>
                <Select
                  value={form.subscriptionTier}
                  onValueChange={(v) => updateField('subscriptionTier', v as SubscriptionTier)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FREE">Free</SelectItem>
                    <SelectItem value="STANDARD">Standard</SelectItem>
                    <SelectItem value="PREMIUM">Premium</SelectItem>
                    <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-lg border bg-muted/50 p-4">
                <h4 className="mb-2 text-sm font-medium">Tier Limits</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Max Users</p>
                    <p className="font-medium">{tierLimits.maxUsers === 999999 ? 'Unlimited' : tierLimits.maxUsers}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Max Machines</p>
                    <p className="font-medium">{tierLimits.maxMachines === 999999 ? 'Unlimited' : tierLimits.maxMachines}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Max Warehouses</p>
                    <p className="font-medium">{tierLimits.maxWarehouses === 999999 ? 'Unlimited' : tierLimits.maxWarehouses}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isTrial"
                  checked={form.isTrial}
                  onCheckedChange={(checked) => updateField('isTrial', checked === true)}
                />
                <Label htmlFor="isTrial" className="cursor-pointer">
                  Start as trial account
                </Label>
              </div>

              {form.isTrial && (
                <div className="space-y-2">
                  <Label htmlFor="trialEndsAt">Trial End Date *</Label>
                  <Input
                    id="trialEndsAt"
                    type="date"
                    value={form.trialEndsAt}
                    onChange={(e) => updateField('trialEndsAt', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                  {errors.trialEndsAt && <p className="text-sm text-red-600">{errors.trialEndsAt}</p>}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <User className="h-5 w-5" />
                Initial Admin
              </CardTitle>
              <CardDescription>
                Create the first admin user for this tenant. An invitation email will be sent.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="adminEmail">Admin Email *</Label>
                <Input
                  id="adminEmail"
                  type="email"
                  value={form.initialAdmin.email}
                  onChange={(e) => updateAdminField('email', e.target.value)}
                  placeholder="admin@acmevending.com"
                />
                {errors['initialAdmin.email'] && (
                  <p className="text-sm text-red-600">{errors['initialAdmin.email']}</p>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="adminFirstName">First Name *</Label>
                  <Input
                    id="adminFirstName"
                    value={form.initialAdmin.firstName}
                    onChange={(e) => updateAdminField('firstName', e.target.value)}
                    placeholder="John"
                  />
                  {errors['initialAdmin.firstName'] && (
                    <p className="text-sm text-red-600">{errors['initialAdmin.firstName']}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="adminLastName">Last Name *</Label>
                  <Input
                    id="adminLastName"
                    value={form.initialAdmin.lastName}
                    onChange={(e) => updateAdminField('lastName', e.target.value)}
                    placeholder="Smith"
                  />
                  {errors['initialAdmin.lastName'] && (
                    <p className="text-sm text-red-600">{errors['initialAdmin.lastName']}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {submitError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" asChild>
              <Link to="/platform/tenants">Cancel</Link>
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Tenant
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
